import { uniq } from '@skytech/utils/list-operations'

import type { AssignmentStatus } from './types'

export function getPolicyStatus({
  policyId,
  costObjectList,
}: {
  policyId: number
  costObjectList: { costObjectId: number; policyIds: number[] | null }[]
}): AssignmentStatus {
  let status: AssignmentStatus = 'not_assigned'
  const assignedCostObjects: number[] = []

  costObjectList.forEach(el => {
    const isPolicyAssigned = el.policyIds?.includes(policyId)
    if (!isPolicyAssigned) return
    if (status === 'not_assigned') status = 'partially_assigned'
    assignedCostObjects.push(el.costObjectId)
  })

  if (assignedCostObjects.length === costObjectList.length) {
    status = 'assigned'
  }

  return status
}

export function getUpdatedPoliciesByCostObjectId({
  policyId,
  costObjectList,
  policyStatus,
}: {
  policyId: number
  costObjectList: { costObjectId: number; policyIds: number[] | null }[]
  policyStatus: AssignmentStatus
}): { costObjectId: number; policyIds: number[] | null }[] {
  if (policyStatus === 'assigned') {
    return costObjectList.map(costObject => ({
      ...costObject,
      policyIds:
        costObject.policyIds?.filter(existingPolicyId => existingPolicyId !== policyId) || null,
    }))
  }

  return costObjectList.map(costObject => ({
    ...costObject,
    policyIds: [...(costObject.policyIds || []), policyId],
  }))
}

export function getUpdatedTelePoliciesByCostObjectId({
  policyId,
  costObjectList,
  policyStatus,
}: {
  policyId: number
  costObjectList: { costObjectId: number; policyIds: number[] | null }[]
  policyStatus: AssignmentStatus
}): { costObjectId: number; policyIds: number[] | null }[] {
  if (policyStatus === 'assigned') {
    return costObjectList.map(costObject => ({
      ...costObject,
      policyIds: null,
    }))
  }

  return costObjectList.map(costObject => ({
    ...costObject,
    policyIds: [policyId],
  }))
}

export function getCostObjectIdsByPolicy(
  costObjectList: { costObjectId: number; policyIds: number[] | null }[]
) {
  return costObjectList.reduce((acc: Record<number, number[]>, curr) => {
    curr.policyIds?.forEach(policyId => {
      acc[policyId] = [...(acc[policyId] || []), curr.costObjectId]
    })
    return acc
  }, {})
}

export function getAlteredPolicyIds({
  init,
  curr,
  policyIds,
}: {
  init: Record<number, number[]>
  curr: Record<number, number[]>
  policyIds: number[] | null
}) {
  const alteredPolicyIds = policyIds?.reduce(
    (acc: { removed: number[]; assigned: number[] }, policyId) => {
      init[policyId]?.forEach(costObjectId => {
        if (init[policyId]?.includes(costObjectId) && curr[policyId]?.includes(costObjectId)) return
        if (init[policyId]?.includes(costObjectId) && !curr[policyId]?.includes(costObjectId)) {
          acc.removed.push(policyId)
          return
        }
      })

      curr[policyId]?.forEach(costObjectId => {
        if (curr[policyId]?.includes(costObjectId) && init[policyId]?.includes(costObjectId)) return
        if (curr[policyId]?.includes(costObjectId) && !init[policyId]?.includes(costObjectId)) {
          acc.assigned.push(policyId)
          return
        }
      })

      return acc
    },
    { removed: [], assigned: [] }
  )

  return { removed: uniq(alteredPolicyIds?.removed), assigned: uniq(alteredPolicyIds?.assigned) }
}

export function getAffectedCostObjectsIdsByPolicyId({
  initCostObjectList,
  currentCostObjectList,
}: {
  initCostObjectList: { costObjectId: number; policyIds: number[] | null }[]
  currentCostObjectList: { costObjectId: number; policyIds: number[] | null }[]
}) {
  let removedPolicyUsersByPolicyId: Record<number, number[]> = {}
  let assignedPolicyUsersByPolicyId: Record<number, number[]> = {}
  let affectedUserIds: number[] = []

  initCostObjectList.forEach(initCostObjectData => {
    const currentCostObjectData = currentCostObjectList.find(
      costObjectData => costObjectData.costObjectId === initCostObjectData.costObjectId
    )

    if (!currentCostObjectData) return

    const initPolicyIds = initCostObjectData.policyIds || []
    const currentPolicyIds = currentCostObjectData.policyIds || []

    initPolicyIds.forEach(policyId => {
      if (currentPolicyIds.includes(policyId)) return

      removedPolicyUsersByPolicyId[policyId]
        ? removedPolicyUsersByPolicyId[policyId].push(initCostObjectData.costObjectId)
        : (removedPolicyUsersByPolicyId[policyId] = [initCostObjectData.costObjectId])
      affectedUserIds.push(initCostObjectData.costObjectId)
    })

    currentPolicyIds.forEach(policyId => {
      if (initPolicyIds.includes(policyId)) return

      assignedPolicyUsersByPolicyId[policyId]
        ? assignedPolicyUsersByPolicyId[policyId].push(initCostObjectData.costObjectId)
        : (assignedPolicyUsersByPolicyId[policyId] = [initCostObjectData.costObjectId])
      affectedUserIds.push(initCostObjectData.costObjectId)
    })
  })

  return {
    removedPolicyUsersByPolicyId,
    assignedPolicyUsersByPolicyId,
    numberOfAllAffectedCostObjects: uniq(affectedUserIds).length,
  }
}
